import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  contentProductivityService,
  ProductivityFilters,
  ProductivityDetailFilters,
  SaveContentUploadQuota,
} from '@/services/contentProductivityService';

const keys = {
  root: ['content-productivity'] as const,
  report: (filters: ProductivityFilters) => ['content-productivity', 'report', filters] as const,
  quotas: (filters: Pick<ProductivityFilters, 'from' | 'to' | 'userId'>) =>
    ['content-productivity', 'quotas', filters] as const,
};

export const useContentProductivity = (filters: ProductivityFilters) =>
  useQuery({
    queryKey: keys.report(filters),
    queryFn: () => contentProductivityService.getReport(filters),
  });

export const useContentUploadQuotas = (
  filters: Pick<ProductivityFilters, 'from' | 'to' | 'userId'>,
) =>
  useQuery({
    queryKey: keys.quotas(filters),
    queryFn: () => contentProductivityService.getQuotas(filters),
  });

export const useContentContributors = () =>
  useQuery({
    queryKey: [...keys.root, 'contributors'],
    queryFn: contentProductivityService.getContributors,
    staleTime: 5 * 60 * 1000,
  });

export const useContentProductivityDetails = (
  filters: ProductivityDetailFilters | undefined,
) =>
  useQuery({
    queryKey: [...keys.root, 'details', filters],
    queryFn: () => contentProductivityService.getDetails(filters!),
    enabled: !!filters,
  });

export const useSaveContentUploadQuota = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quota }: { id?: number; quota: SaveContentUploadQuota }) =>
      id
        ? contentProductivityService.updateQuota(id, quota)
        : contentProductivityService.createQuota(quota),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.root }),
  });
};

export const useDeleteContentUploadQuota = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: contentProductivityService.deleteQuota,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.root }),
  });
};
